import React, { useMemo } from 'react';

import { GlobalVariables } from '../../../pdf/GlobalVariables';
import SettingsDialog from '../../Layout/Dialogs/SettingsDialog';
import { settingsDataObject } from './UserSettingsDialogConfig';
import { Box, Button, Divider, Menu, Stack, Typography } from '@mui/material';
import i18next from 'i18next';
import UserAvatar from './UserAvatar';

import {
    clearErrors,
    logoutUser,
    saveThemeSetting,
    updateUserProfilePicture,
    updateUserProfileProtection,
} from '../../../Redux/UserSettingsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch, type IGlobalStore } from '../../../Redux/ReduxStore';
import styles from './UserControl.module.scss';
import vars from '../../../vars.module.scss';
import { eplassSliceUserSettingsDialogRefresh } from '../../../Redux/EplassSlice';
import { useDarkMode } from '../../../DarkModeContext';
import { useRuler } from '../../../RulerContext';

// TODO: Adjust UserAvatar size to 40. Current problem: The Client shows the current width + 4px.

/**
 * Component that shows the user's Auth0 profile image and allows them to manage and/or log out of their account.
 */
export default () => {
    const { isDark, toggle: toggleDarkMode } = useDarkMode();
    const { showRuler, toggleRuler } = useRuler();
    const {
        picture: { loading },
        ...rest
    } = useSelector((state: IGlobalStore) => state.userSettings);

    const refreshDialog = useSelector(eplassSliceUserSettingsDialogRefresh);

    const username = GlobalVariables.authenticatedUser?.name || '';
    const first_name = GlobalVariables.authenticatedUser?.firstName || '';
    const last_name = GlobalVariables.authenticatedUser?.lastName || '';
    const email = GlobalVariables.authenticatedUser?.email || '';
    const last_password_reset = GlobalVariables.authenticatedUser?.lastPasswordReset || '';
    const dataProtection = rest.data?.dataProtection || '0';
    const picture = rest.data?.picture;

    /** State slice that controls whether the user popup is shown. */
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    /** State slice that controls whether the settings dialog is shown. */
    const [openSettingsDialog, setOpenSettingsDialog] = React.useState<boolean>(false);

    /** Allows reducers on a redux slice to be called. */
    const dispatch: AppDispatch = useDispatch();

    const userData = useSelector((state: IGlobalStore) => state.userSettings.data);

    const userLoading = useSelector((state: IGlobalStore) => state.userSettings.loading);

    React.useEffect(() => {
        if (GlobalVariables.isVisualStudioCode) {
            GlobalVariables.authenticatedUser = {
                adCommonName: 'InfoClientDEV',
                name: 'Anonymous',
                nickname: 'anonymous',
                picture:
                    'https://s.gravatar.com/avatar/63ea0b3d7e56ea71fb18cfbaa8968000?s=480&r=pg&d=https%3A%2F%2Fcdn.auth0.com%2Favatars%2Fcs.png',
                sub: 'auth0|anonymous',
                updated_at: '2022-12-21T08:10:25.251Z',
                fullname: 'Anonymous',
                dataProtection: '1',
                firstName: 'Infoclient',
                lastName: 'dev',
                lastPasswordReset: '13/03/2019',
                email: 'local@mail.de',
            };
        } else {
            GlobalVariables.authenticatedUser = userData;
        }
    }, []);

    /**
     * Makes the menu visible.
     * @param e - React Click Event.
     */
    const handleUserBtnClick = (e: React.MouseEvent<HTMLButtonElement>) =>
        setAnchorEl(e.currentTarget);

    /**
     * Logs the user out of the application.
     */
    const handleLogout = () => {
        dispatch(logoutUser());
    };

    /**
     * Opens settings dialog.
     */
    const handleClickSettingsDialogOpen = () => {
        GlobalVariables.disableKeyboardShortcuts = true;
        setOpenSettingsDialog(true);
    };

    /**
     * Closes settings dialog.
     */
    const handleCloseSettingsDialog = () => {
        GlobalVariables.disableKeyboardShortcuts = false;
        setOpenSettingsDialog(false);
        dispatch(clearErrors(null));
    };

    /**
     * Changes the switch's state.
     * @param e React Change Event.
     */
    const handleChangeSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(updateUserProfileProtection(e.target.checked ? '1' : '0'));
    };

    /**
     * Updates User's Avatar.
     * @param e React Change Event.
     */
    const handleChangeImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) dispatch(updateUserProfilePicture(e.target.files[0]));
    };

    const settingsDialogData = useMemo(() => {
        return settingsDataObject({
            username,
            first_name,
            last_name,
            email,
            last_password_reset,
            dataProtection,
            pictureLoading: loading,
            picture,
            handleChangeImage,
            handleChangeSwitch,
            isDark,
            handleToggleDarkMode: () => {
                toggleDarkMode();
                dispatch(saveThemeSetting(!isDark ? 'dark' : 'light'));
            },
            showRuler,
            handleToggleRuler: toggleRuler,
        });
    }, [loading, openSettingsDialog, refreshDialog, isDark, showRuler]);

    if (userLoading) {
        return <></>;
    }

    return (
        <>
            <Button
                sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                    alignItems: 'center',
                    padding: '8px 24px 8px 8px',
                }}
                onClick={handleUserBtnClick}
            >
                <UserAvatar
                    size={36}
                    loading={loading}
                    picture={picture}
                    sxProps={{ fontSize: '12px' }}
                />
            </Button>

            <Menu open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} anchorEl={anchorEl}>
                <Stack
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        maxWidth: 344,
                    }}
                >
                    <Stack
                        className={styles.stack}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        <Box>
                            <UserAvatar size={60} loading={loading} picture={picture} />
                        </Box>

                        <Stack
                            sx={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                p: 2,
                            }}
                        >
                            <Typography
                                className={styles.font}
                                style={{
                                    fontSize: 16,
                                    lineHeight: '22.4px',
                                }}
                            >
                                {username}
                            </Typography>
                            <Typography className={styles.font} style={{ color: vars.concrete }}>
                                {email}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Divider sx={{ width: '100%' }} />

                    <Stack className={styles.stack} style={{ flexDirection: 'column' }}>
                        <Button onClick={handleClickSettingsDialogOpen} className={styles.button}>
                            <Typography className={styles.typography}>
                                {i18next.t('settingsUserDialog.manageAccount')}
                            </Typography>
                        </Button>

                        <Button onClick={handleLogout} className={styles.button}>
                            <Typography className={styles.typography}>
                                {i18next.t('popup.logout')}
                            </Typography>
                        </Button>
                    </Stack>
                </Stack>
            </Menu>
            <SettingsDialog
                open={openSettingsDialog}
                handleClose={handleCloseSettingsDialog}
                data={settingsDialogData}
            />
        </>
    );
};
